import { ModalManager } from '../modalManager/_modalManager';
import { v4 as uuidv4 } from 'uuid';

interface DebugOptions {
  enabled: boolean;
}

const DEBUG_DEFAULT: DebugOptions = {
  enabled: true,
};

class DebugUtil {
  private enabled: boolean;
  private prefix: string;

  constructor(enabled: boolean, prefix: string = '') {
    this.enabled = enabled;
    this.prefix = prefix ? `[${prefix}] ` : '';
  }

  log(...args: any[]): void {
    if (this.enabled) {
      console.debug(this.prefix, ...args);
    }
  }
}

interface MapData {
  alt: string;
  shape: string;
  coords: string;
  href?: string;
  action: {
    'data-aria-label': string;
    content: string;
  };
}

interface ImageMapConfig {
  mapData: MapData[];
}

export class ImageMap {
  private container: Element;
  private data: ImageMapConfig | null = null;
  private actionContainer: HTMLDivElement | null = null;
  private boundHandleFocusOut: EventListener;
  private boundHandleResize: () => void;
  private debug: DebugUtil;
  private modalManager: ModalManager;
  private isModalOpen: boolean = false;

  // イベントハンドラーをクラスプロパティとして定義（アロー関数）
  private handleLinkClick = (e: Event): void => {
    this.debug.log('リンクがクリックされました', e);
    this.handleLinkInteraction(e);
  };

  private handleLinkKeydown = (e: KeyboardEvent): void => {
    this.debug.log('リンクでキーが押されました', e.key);
    if (e.key === 'Enter') {
      e.preventDefault();
      this.debug.log('Enterキーが押されました、モーダルを開きます');
      this.openModalForLink(e);
    } else if (e.key === ' ') {
      e.preventDefault();
      this.debug.log(
        'スペースキーが押されました、インタラクションを処理します'
      );
      this.handleLinkInteraction(e);
    }
  };

  private handleLinkHover = (e: Event): void => {
    this.debug.log('リンクにマウスが重なりました', e);
    this.openModalForLink(e);
  };

  constructor(container: Element, debugOptions: DebugOptions = DEBUG_DEFAULT) {
    if (!container) {
      throw new Error('コンテナ要素が見つかりません');
    }

    // 既に同一containerでactionContainerが存在するかチェック（重複初期化防止）
    if (container.querySelector('.ist-js__imageMapActionContents')) {
      throw new Error('このコンテナでImageMapは既に初期化されています。');
    }

    this.container = container;
    this.debug = new DebugUtil(debugOptions.enabled, 'ImageMap');
    this.debug.log('コンストラクタが呼び出されました', { container });

    this.modalManager = new ModalManager();

    this.boundHandleFocusOut = this.handleFocusOut.bind(this) as EventListener;
    this.boundHandleResize = this.debounce(
      this.updatePositions.bind(this),
      250
    );

    this.init();
  }

  private init(): void {
    this.debug.log('初期化中');
    const jsonScript = this.container.querySelector('.ist-imageMap__data');
    if (!jsonScript) {
      this.debug.log('マップデータ要素が見つかりません');
      console.warn('マップデータ要素が見つかりません');
      return;
    }

    try {
      this.data = JSON.parse(jsonScript.textContent || '');
      this.debug.log('マップデータを解析しました:', this.data);

      if (
        !this.data?.mapData ||
        !Array.isArray(this.data?.mapData) ||
        this.data?.mapData.length === 0
      ) {
        this.debug.log('mapDataが空または無効のため、初期化を中断します');
        return;
      }

      const img = this.container.querySelector('img');
      if (img) {
        this.debug.log('画像が見つかりました:', img);
        if (img.complete) {
          this.debug.log('画像は既に読み込まれています');
          this.setupMap();
        } else {
          this.debug.log('画像の読み込みを待機中');
          img.addEventListener('load', () => {
            this.debug.log('画像が読み込まれました');
            this.setupMap();
          });
        }
      } else {
        this.debug.log('コンテナ内に画像が見つかりません');
      }
    } catch (e) {
      this.debug.log('マップデータの解析でエラーが発生:', e);
      console.error('マップデータの解析に失敗:', e);
    }
  }

  private setupMap(): void {
    this.debug.log('アクションコンテナのセットアップ中');
    this.createActionContainer();
    if (this.actionContainer) {
      // actionContainer が存在する場合のみ続行
      this.setupEventListeners();
      this.updatePositions();
    }
  }

  private createActionContainer(): void {
    this.debug.log('アクションコンテナを作成中');
    if (!this.data) {
      this.debug.log('マップデータが利用できません');
      console.warn('マップデータが利用できません');
      return;
    }

    if (!Array.isArray(this.data.mapData)) {
      this.debug.log('マップデータの構造が無効です');
      console.error('マップデータの構造が無効です');
      return;
    }

    if (this.data.mapData.length === 0) {
      this.debug.log('mapDataが空のため、アクションコンテナを作成しません');
      return;
    }

    this.actionContainer = document.createElement('div');
    this.actionContainer.classList.add('ist-js__imageMapActionContents');

    this.data.mapData.forEach((item, index) => {
      const link = document.createElement('a');
      link.setAttribute('href', item.href || '#');
      // `data-aria-label` を `aria-label` として設定
      link.setAttribute(
        'aria-label',
        item.action['data-aria-label'] || item.alt
      );
      link.setAttribute('role', 'link');
      link.setAttribute('tabindex', '0');
      link.setAttribute('data-index', index.toString());
      link.classList.add('ist-imageMap__actionLink');

      this.debug.log('アクションリンクを作成しました:', { index, link });
      this.actionContainer?.appendChild(link);
    });

    this.container.appendChild(this.actionContainer);
    this.debug.log('アクションコンテナをコンテナに追加しました');
  }

  private setupEventListeners(): void {
    this.debug.log('イベントリスナーを設定中');
    if (!this.actionContainer) {
      this.debug.log(
        'actionContainerが存在しないため、イベントリスナーの設定をスキップします'
      );
      return;
    }
    const links = this.actionContainer.querySelectorAll('a');

    links.forEach((link) => {
      link.addEventListener('click', this.handleLinkClick);
      link.addEventListener('keydown', this.handleLinkKeydown);
      link.addEventListener('mouseenter', this.handleLinkHover);
    });

    this.container.addEventListener('focusout', this.boundHandleFocusOut);
    window.addEventListener('resize', this.boundHandleResize);
    document.addEventListener('keydown', this.handleGlobalKeyDown);
    this.debug.log('イベントリスナーを追加しました');
  }

  private handleLinkInteraction(event: Event): void {
    this.debug.log('リンクのインタラクションを検出', event);
    event.preventDefault();
    // 必要な処理があればここに記述
  }

  private openModalForLink(event: Event): void {
    this.debug.log('モーダルを開く処理を開始', event);
    event.preventDefault();
    const link = event.currentTarget as HTMLAnchorElement | null;
    if (!link) {
      this.debug.log('イベントターゲットにリンク要素が見つかりません');
      return;
    }

    if (this.isModalOpen) {
      this.debug.log(
        'モーダルは既に開いています、新しいモーダル作成をスキップします'
      );
      return;
    }

    const index = parseInt(link.getAttribute('data-index') || '0', 10);
    const mapData = this.data?.mapData[index];

    if (!mapData) {
      this.debug.log(`インデックス ${index} のマップデータが見つかりません`);
      return;
    }

    const content = mapData.action.content;
    const ariaLabel = mapData.action['data-aria-label'];

    if (!content) {
      this.debug.log(`インデックス ${index} のコンテンツが定義されていません`);
      return;
    }

    this.debug.log('コンテンツが見つかりました、モーダルを表示します');

    // 一意のIDを生成
    const uniqueModalId = `modal-${uuidv4()}`;

    this.modalManager.showModal({
      content: content,
      modalId: uniqueModalId, // 一意なIDを設定
      modalAriaLabel: ariaLabel,
      triggerElement: link,
      onClose: () => {
        this.debug.log(
          'モーダルが閉じられました、リンクにフォーカスを戻します'
        );
        link.focus();
        this.isModalOpen = false;
      },
    });

    this.isModalOpen = true;
  }

  private handleGlobalKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.debug.log('Escapeキーが押されました');
      // ModalManagerがEscキーでモーダルを閉じるため、ここでは特に処理不要
    }
  };

  private handleFocusOut(event: FocusEvent): void {
    this.debug.log('フォーカスアウトイベントを検出', event);
    // フォーカスアウト時の処理が必要ならここに記述
  }

  private debounce(func: Function, wait: number): () => void {
    let timeout: NodeJS.Timeout;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(), wait);
    };
  }

  private updatePositions(): void {
    this.debug.log('リンクの位置を更新中');
    const img = this.container.querySelector('img');
    if (!img || !this.data || !this.actionContainer) return;

    const naturalWidth = img.naturalWidth;
    const currentWidth = img.offsetWidth;
    const scale = currentWidth / naturalWidth;

    const links = this.actionContainer.querySelectorAll('a');
    links.forEach((link) => {
      const index = parseInt(link.getAttribute('data-index') || '0', 10);
      const mapData = this.data?.mapData[index];
      if (!mapData) return;
      const coords = mapData.coords.split(',').map((num) => parseInt(num, 10));
      const [x1, y1, x2, y2] = coords;
      const scaledX1 = x1 * scale;
      const scaledY1 = y1 * scale;
      const scaledX2 = x2 * scale;
      const scaledY2 = y2 * scale;
      const width = scaledX2 - scaledX1;
      const height = scaledY2 - scaledY1;

      const linkElement = link as HTMLElement;
      linkElement.style.position = 'absolute';
      linkElement.style.left = `${scaledX1}px`;
      linkElement.style.top = `${scaledY1}px`;
      linkElement.style.width = `${width}px`;
      linkElement.style.height = `${height}px`;
    });

    this.debug.log('リンクの位置を更新しました');
  }

  public destroy(): void {
    this.debug.log('ImageMapインスタンスを破棄中');
    if (!this.actionContainer) return;

    const links = this.actionContainer.querySelectorAll('a');
    links.forEach((link) => {
      link.removeEventListener('click', this.handleLinkClick);
      link.removeEventListener('keydown', this.handleLinkKeydown);
      link.removeEventListener('mouseenter', this.handleLinkHover);
    });

    this.container.removeEventListener('focusout', this.boundHandleFocusOut);
    window.removeEventListener('resize', this.boundHandleResize);
    document.removeEventListener('keydown', this.handleGlobalKeyDown);

    this.modalManager.hideModal();

    if (this.actionContainer) {
      this.actionContainer.remove();
      this.actionContainer = null;
    }

    this.isModalOpen = false;

    this.debug.log('ImageMapインスタンスを破棄しました');
  }
}
